import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  EmptyState,
  ErrorState,
  LoadingState,
  Page,
  PageHeader,
  PageSection,
  Table,
} from '@/components';
import { useDeleteFlow, useFlows } from '@/hooks/useFlows';
import { FlowJsonEditor } from '@/components/flows/FlowJsonEditor';

const nowIso = () => new Date().toISOString();

export function Flows() {
  const navigate = useNavigate();
  const query = useFlows();
  const del = useDeleteFlow();
  const [isJsonEditorOpen, setIsJsonEditorOpen] = useState(false);


  const handleDelete = async (flowId: string) => {
    try {
      await del.mutateAsync(flowId);
      toast.success('Fluxo removido');
    } catch {
      toast.error('Não foi possível remover o fluxo');
    }
  };

  return (
    <Page>
      <PageHeader
        title="Fluxos"
        description="Automação por lista de etapas (inbound e schedule) para WhatsApp e Web Chat."
        right={
          <Button variant="primary" onClick={() => setIsJsonEditorOpen(true)}>
            Criar Fluxo
          </Button>
        }
      />

      <PageSection>
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="font-semibold">Lista de fluxos</div>
              <Badge variant="neutral">MVP</Badge>
            </div>
          </CardHeader>
          <CardBody>
            {query.isLoading ? (
              <LoadingState title="Carregando fluxos" />
            ) : query.isError ? (
              <ErrorState title="Falha ao carregar fluxos" onRetry={() => query.refetch()} />
            ) : !query.data || query.data.length === 0 ? (
              <EmptyState
                title="Nenhum fluxo cadastrado"
                description="Crie um fluxo inbound ou agendado para começar."
                action={
                  <Button variant="primary" onClick={() => setIsJsonEditorOpen(true)}>
                    Criar Fluxo
                  </Button>
                }
              />
            ) : (
              <Table>
                <thead>
                  <tr>
                    <th>Nome</th>
                    <th>Status</th>
                    <th>Trigger</th>
                    <th>Atualizado</th>
                    <th style={{ width: 140 }}>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {query.data.map((f) => (
                    <tr key={f.id}>
                      <td className="font-medium">{f.name}</td>
                      <td>
                        <Badge variant={f.status === 'active' ? 'success' : f.status === 'paused' ? 'warning' : 'neutral'}>
                          {f.status}
                        </Badge>
                      </td>
                      <td>
                        <Badge variant="neutral">{f.trigger_type || f.trigger?.type}</Badge>
                      </td>
                      <td className="text-gray-500">{(f.updated_at || nowIso()).slice(0, 10)}</td>
                      <td>
                        <div className="flex items-center" style={{ gap: 8 }}>
                          <Button variant="secondary" onClick={() => navigate(`/flows/${f.id}`)}>
                            Editar
                          </Button>
                          <Button variant="secondary" onClick={() => handleDelete(f.id)} disabled={del.isLoading}>
                            Remover
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </CardBody>
        </Card>
      </PageSection>

      {isJsonEditorOpen && (
        <FlowJsonEditor
          onClose={() => setIsJsonEditorOpen(false)}
          onSuccess={() => {
            setIsJsonEditorOpen(false);
            query.refetch();
            toast.success('Fluxo criado com sucesso!');
          }}
        />
      )}
    </Page>
  );
}
